import { RefMonitorContext } from "./components/RefMonitorProvider";
import {
  cloneElement,
  ComponentType,
  createElement,
  isValidElement,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
} from "react";

export type ConnectRef = (
  id: string,
  namespace: string,
  node: ReactNode,
) => ReactElement | undefined;

type WithConnectRef = {
  connectRef: ConnectRef;
};

/**
 * HOC that injects connectRef, which registers DOM nodes with the shared RefManager.
 */
const RefSource = <Props extends object>(
  WrappedComponent: ComponentType<Props & WithConnectRef>,
) => {
  const RefSourceComponent = (props: Props) => {
    const refManager = useContext(RefMonitorContext);

    const connectRef: ConnectRef = useCallback(
      (id, namespace, node) => {
        if (isValidElement(node)) {
          return cloneElement(node as ReactElement, {
            ref: (refNode: Element | null) => {
              refManager?.setRef(id, namespace, refNode);
            },
          });
        }
      },
      [refManager],
    );

    return createElement(WrappedComponent, {
      ...props,
      connectRef,
    } as Props & WithConnectRef);
  };

  const wrappedName =
    WrappedComponent.displayName || WrappedComponent.name || "Component";
  RefSourceComponent.displayName = `RefSource(${wrappedName})`;

  return RefSourceComponent;
};

export default RefSource;
